import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

interface FormResponse {
  code?: string;
  meta?: { message?: string; detail?: string };
  message?: string;
}

const guides = [
  {
    icon: "ri-compass-3-line",
    title: "浏览与发现",
    text: "从首页焦点推荐或分类页开始，按类型、年份、标签自由组合筛选，也可以直接用顶部搜索框找关键词。",
  },
  {
    icon: "ri-file-list-3-line",
    title: "查看详情",
    text: "每部短剧都有独立的详情页，包含剧情简介、集数与时长、主创信息，以及基于标签相似度的相关推荐。",
  },
  {
    icon: "ri-share-forward-line",
    title: "收藏与分享",
    text: "详情页与资讯页都支持分享功能，浏览器支持时调用系统分享，否则自动复制链接，方便收藏与转发。",
  },
  {
    icon: "ri-information-line",
    title: "关于播放",
    text: "本站是内容推荐与资讯平台，不提供视频播放服务，站内所有作品与人物均为虚构演示内容。",
  },
];

export default function AboutPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch("https://readdy.ai/api/form/db7sq4omkl3spn8smchg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ?? parsed?.message ?? parsed?.meta?.detail ?? responseText ?? "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "提交未成功，请检查填写内容后重试。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后再试。");
    }
  };

  return (
    <div className="w-full">
      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-8 md:py-20">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-400" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="font-medium text-foreground-700">关于</span>
          </nav>

          <h1 className="mt-5 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
            关于 ai短剧
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            ai短剧是一个聚焦人工智能创作短剧的内容推荐与资讯聚合平台，服务喜爱新兴影视形式、
            关注 AI 短视频作品的年轻观众、影视行业从业者与内容创作者。我们希望通过清晰的分类、
            扎实的作品资料与及时的行业资讯，让每一位观众都能更快找到自己想看的那一部。
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-7xl px-4 py-14 md:px-8 md:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-accent-600">
              <span className="h-1 w-1 rounded-full bg-accent-500"></span>
              Our Positioning
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              做 AI 短剧的内容坐标
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-foreground-600 md:text-base">
              当每天都有新的 AI 短剧上线，「找到值得看的」比「看更多」更重要。
              我们用统一的资料结构、可组合的筛选条件与基于标签的推荐逻辑，
              把分散的作品与资讯整理成一张清晰的地图。
            </p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              本站不设登录、会员与支付，也不提供视频播放功能，所有内容与人物姓名均为虚构示例，
              用于展示平台的内容组织方式与浏览体验。
            </p>

            <div className="mt-8 grid grid-cols-3 gap-4">
              {[
                { value: "9", label: "内容分类" },
                { value: "每日", label: "资讯更新" },
                { value: "0", label: "登录门槛" },
              ].map((item) => (
                <div key={item.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
                  <p className="font-heading text-xl font-bold text-primary-600">{item.value}</p>
                  <p className="mt-1 text-xs text-foreground-500">{item.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">使用说明</h2>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {guides.map((item) => (
                <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                    <i className={`${item.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-500">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-8 md:py-20">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-accent-600">
                <span className="h-1 w-1 rounded-full bg-accent-500"></span>
                Contact
              </span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                联系与反馈
              </h2>
              <p className="mt-5 text-sm leading-relaxed text-foreground-600 md:text-base">
                如果你想推荐一部 AI 短剧、纠正信息、或者提出合作与建议，
                可以通过下面的表单留言，我们会尽快查看并回复。
              </p>

              <div className="mt-8 flex flex-col gap-4">
                <a
                  href="mailto:hello@ai-duanju.example.com"
                  className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className="ri-mail-send-line text-xl"></i>
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-400">邮件联系</span>
                    <span className="block text-sm font-medium text-foreground-800">
                      hello@ai-duanju.example.com
                    </span>
                  </span>
                </a>

                <div className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                    <i className="ri-time-line text-xl"></i>
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-400">回复时间</span>
                    <span className="block text-sm font-medium text-foreground-800">
                      工作日 10:00 - 18:00，一般在 1-2 个工作日内回复
                    </span>
                  </span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <form
                id="about-feedback-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
              >
                <h3 className="font-heading text-lg font-bold text-foreground-950">提交反馈</h3>
                <p className="mt-2 text-xs text-foreground-500">带 * 为必填项，留言请控制在 500 字以内。</p>

                <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="feedback-name" className="mb-2 block text-sm font-medium text-foreground-700">
                      你的称呼 <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="feedback-name"
                      name="name"
                      type="text"
                      required
                      placeholder="怎么称呼你"
                      className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-400"
                    />
                  </div>

                  <div>
                    <label htmlFor="feedback-email" className="mb-2 block text-sm font-medium text-foreground-700">
                      邮箱 <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="feedback-email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于接收回复"
                      className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-400"
                    />
                  </div>
                </div>

                <div className="mt-5">
                  <label htmlFor="feedback-topic" className="mb-2 block text-sm font-medium text-foreground-700">
                    反馈类型
                  </label>
                  <select
                    id="feedback-topic"
                    name="topic"
                    defaultValue="内容推荐"
                    className="h-11 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                  >
                    <option value="内容推荐">内容推荐</option>
                    <option value="信息纠错">信息纠错</option>
                    <option value="合作洽谈">合作洽谈</option>
                    <option value="产品建议">产品建议</option>
                    <option value="其他">其他</option>
                  </select>
                </div>

                <div className="mt-5">
                  <label htmlFor="feedback-message" className="mb-2 block text-sm font-medium text-foreground-700">
                    留言内容 <span className="text-accent-600">*</span>
                  </label>
                  <textarea
                    id="feedback-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请描述你想反馈的短剧、问题或建议（不超过 500 字）"
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 p-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-400"
                  ></textarea>
                </div>

                <input
                  className="alt-field-helper"
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === "success" && (
                  <div className="mt-5 flex items-start gap-3 rounded-md border border-primary-200 bg-primary-50 p-4">
                    <i className="ri-checkbox-circle-fill text-lg text-primary-600"></i>
                    <p className="text-sm text-primary-800">
                      感谢你的反馈！我们已经收到你的留言，会尽快通过邮箱与你联系。
                    </p>
                  </div>
                )}

                {status === "error" && (
                  <div className="mt-5 flex items-start gap-3 rounded-md border border-accent-200 bg-accent-50 p-4">
                    <i className="ri-error-warning-fill text-lg text-accent-600"></i>
                    <p className="text-sm text-accent-800">{formError}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
                  <span className="whitespace-nowrap">
                    {status === "submitting" ? "提交中…" : "提交反馈"}
                  </span>
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-7xl px-4 py-14 md:px-8 md:py-16">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <Link
            to="/category"
            className="group rounded-lg border border-background-200 bg-background-50 p-6 transition-colors hover:border-primary-300"
          >
            <i className="ri-compass-3-line text-2xl text-primary-500"></i>
            <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">浏览短剧分类</h3>
            <p className="mt-2 text-sm text-foreground-500">按类型、年份与标签筛选，找到你想看的作品。</p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-600">
              去发现
              <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5"></i>
            </span>
          </Link>

          <Link
            to="/news"
            className="group rounded-lg border border-background-200 bg-background-50 p-6 transition-colors hover:border-primary-300"
          >
            <i className="ri-newspaper-line text-2xl text-primary-500"></i>
            <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">阅读短剧资讯</h3>
            <p className="mt-2 text-sm text-foreground-500">行业动态、作品观察与创作手记，一页看完。</p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-600">
              去看资讯
              <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5"></i>
            </span>
          </Link>

          <Link
            to="/search"
            className="group rounded-lg border border-background-200 bg-background-50 p-6 transition-colors hover:border-primary-300"
          >
            <i className="ri-search-line text-2xl text-primary-500"></i>
            <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">直接搜索</h3>
            <p className="mt-2 text-sm text-foreground-500">输入片名或关键词，短剧与资讯一次搜全。</p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-600">
              去搜索
              <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5"></i>
            </span>
          </Link>
        </div>
      </section>
    </div>
  );
}